// Interior Hero — Animated layout: scroll-driven background color transition.
// As the hero scrolls out of view (top edge moves up past the viewport top),
// its background color linearly interpolates from the configured starting
// color (data-bg-color) toward white. Respects prefers-reduced-motion by
// skipping the listener entirely, leaving the static configured color in place.
//
// data-bg-color is the item's raw ColorPicker hex (src/Rithm.Web/uSync/v17/DataTypes/
// InteriorHeroAnimatedColorPicker.config) — it should match that same color's --color-bg
// in themes.postcss, since the CSS cascade renders the resting-state color and this module
// only takes over once scrolling begins. Keep both in sync if a color option ever changes.

const heroes = /** @type {NodeListOf<HTMLElement>} */(document.querySelectorAll('[data-interior-hero-animated]'))

// Fraction of the hero's own height the transition completes over — 1 would take the hero's
// full height to reach white, which reads as sluggish given how tall this hero renders.
// Lower = faster. Tune this single constant to adjust the feel.
const TRANSITION_HEIGHT_FRACTION = 0.5

if (heroes.length && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    /** @param {number} value @param {number} min @param {number} max */
    const clamp = (value, min, max) => Math.min(Math.max(value, min), max)

    /** @param {string} hex */
    function parseHex(hex) {
        const normalized = hex.replace('#', '')
        const r = parseInt(normalized.substring(0, 2), 16)
        const g = parseInt(normalized.substring(2, 4), 16)
        const b = parseInt(normalized.substring(4, 6), 16)
        return { r, g, b }
    }

    /** @type {WeakMap<HTMLElement, number>} */
    const lastProgress = new WeakMap()

    /** @param {HTMLElement} el */
    function updateHero(el) {
        const hex = el.getAttribute('data-bg-color')
        if (!hex) return
        const rect = el.getBoundingClientRect()
        const progress = clamp((0 - rect.top) / (rect.height * TRANSITION_HEIGHT_FRACTION), 0, 1)

        // Once fully settled (e.g. scrolled all the way past, or still at the top), further
        // scroll events recompute the identical clamped value — skip the redundant work.
        if (lastProgress.get(el) === progress) return
        lastProgress.set(el, progress)

        const { r, g, b } = parseHex(hex)
        const nextR = r + (255 - r) * progress
        const nextG = g + (255 - g) * progress
        const nextB = b + (255 - b) * progress

        el.style.backgroundColor = `rgb(${nextR}, ${nextG}, ${nextB})`
    }

    // rAF-throttle: scroll can fire many times per frame, but a frame only paints once,
    // so `ticking` collapses any events that arrive before the scheduled frame runs into
    // a single update instead of recomputing/rewriting styles for each one.
    let ticking = false
    function onScroll() {
        if (ticking) return
        ticking = true
        requestAnimationFrame(() => {
            heroes.forEach(updateHero)
            ticking = false
        })
    }

    // Set the correct starting state immediately (e.g. on reload mid-scroll).
    heroes.forEach(updateHero)
    window.addEventListener('scroll', onScroll, { passive: true })
}
